import { useState, useEffect, useCallback } from "react"
import { List, Button, Image, Popconfirm, Space, Spin } from "antd"
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { ContentWrapper } from "../../components/Wrappers"
import { getBackupFiles, deleteBackupFile, downloadBackupFile, type BackupFile } from "../../services/backup"

type BackupFileListProps = {
  type: "word" | "image"
  category: string
}

export const BackupFileList: React.FC<BackupFileListProps> = ({
  type,
  category,
}) => {
  const { t } = useTranslation()
  const [files, setFiles] = useState<BackupFile[]>([])
  const [loading, setLoading] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewUrl, setPreviewUrl] = useState("")

  const fetchFiles = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getBackupFiles(category)
      setFiles(data)
    } catch {
      setFiles([])
    } finally {
      setLoading(false)
    }
  }, [category])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  const handleDelete = async (fileName: string) => {
    try {
      await deleteBackupFile(category, fileName)
      setFiles((prev) => prev.filter((f) => f.name !== fileName))
    } catch {
      // error is logged in the service
    }
  }

  return (
    <ContentWrapper>
      <Spin spinning={loading}>
        <List
          itemLayout="horizontal"
          dataSource={files}
          renderItem={(item) => (
            <List.Item
              actions={[
                ...(type === "image" && item.url
                  ? [
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setPreviewUrl(item.url!)
                          setPreviewVisible(true)
                        }}
                      >
                        {t("backup.preview")}
                      </Button>,
                    ]
                  : []),
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadBackupFile(category, item.name)}
                >
                  {t("backup.download")}
                </Button>,
                <Popconfirm
                  title={t("backup.deleteConfirm")}
                  onConfirm={() => handleDelete(item.name)}
                  okText={t("backup.delete")}
                  cancelText={t("backup.cancel")}
                >
                  <Button type="text" danger icon={<DeleteOutlined />}>
                    {t("backup.delete")}
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={item.name}
                description={
                  <Space>
                    <span>
                      {`${t("backup.fileSize")}: ${Math.floor(Number(item.size) / 1024)} KB`}
                    </span>
                    <span>{`${t("backup.date")}: ${new Date(item.createdAt).toLocaleDateString("sk-SK")}`}</span>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Spin>
      {type === "image" && (
        <Image
          style={{ display: "none" }}
          preview={{
            visible: previewVisible,
            src: previewUrl,
            onVisibleChange: (visible) => setPreviewVisible(visible),
          }}
        />
      )}
    </ContentWrapper>
  )
}
